import React from 'react';
import { Layout } from '../../components/Layout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Database, Cloud, HardDrive, RefreshCw, Shield, ArrowRight, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
export function Backup() {
  return <Layout data-id="element-4872">
      <div className="container mx-auto px-4 py-12" data-id="element-4873">
        {/* Hero */}
        <div className="mb-12" data-id="element-4874">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-teal-500/10 text-teal-500 text-xs font-medium mb-4 border border-teal-500/20" data-id="element-4875">
            <Database className="h-3 w-3 mr-2" data-id="element-4876" />
            Tools Library
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-100 mb-4" data-id="element-4877">
            Backup Tools
          </h1>
          <p className="text-xl text-slate-400 max-w-3xl" data-id="element-4878">
            Reliable backup solutions for personal and business use. Data loss
            is inevitable; backups are your only insurance.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8" data-id="element-4879">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12" data-id="element-4880">
            {/* Why This Matters */}
            <section data-id="element-4881">
              <h2 className="text-2xl font-bold text-slate-100 mb-4" data-id="element-4882">
                Why This Matters
              </h2>
              <div className="bg-slate-900/50 border-l-4 border-teal-500 p-6 rounded-r-lg" data-id="element-4883">
                <p className="text-slate-300 mb-4" data-id="element-4884">
                  Hardware fails, ransomware encrypts files, and accidents
                  happen. Without a robust backup strategy, you risk losing
                  irreplaceable photos, documents, and business data forever.
                </p>
                <ul className="space-y-2 text-slate-400" data-id="element-4885">
                  <li className="flex items-start" data-id="element-4886">
                    <span className="text-teal-500 mr-2" data-id="element-4887">•</span> Ransomware is
                    the #1 threat to data availability
                  </li>
                  <li className="flex items-start" data-id="element-4888">
                    <span className="text-teal-500 mr-2" data-id="element-4889">•</span> Cloud sync
                    (Dropbox/Google Drive) is NOT a backup
                  </li>
                  <li className="flex items-start" data-id="element-4890">
                    <span className="text-teal-500 mr-2" data-id="element-4891">•</span> Testing
                    restores is just as important as backing up
                  </li>
                </ul>
              </div>
            </section>

            {/* Tool Categories */}
            <section data-id="element-4892">
              <h2 className="text-2xl font-bold text-slate-100 mb-6" data-id="element-4893">
                Recommended Tools
              </h2>
              <div className="grid gap-6" data-id="element-4894">
                <ToolCategory title="Cloud Backup Services" icon={Cloud} desc="Automatic, offsite backup for your computer." tools={[{
                name: 'Backblaze',
                note: 'Unlimited backup, affordable'
              }, {
                name: 'iDrive',
                note: 'Multi-device support, versioning'
              }, {
                name: 'Carbonite',
                note: 'Business-focused features'
              }]} safety="Ensure encryption at rest and use a private encryption key if possible." data-id="element-4895" />
                <ToolCategory title="Local Backup Software" icon={HardDrive} desc="Software to manage backups to external drives or NAS." tools={[{
                name: 'Time Machine',
                note: 'Built-in macOS solution'
              }, {
                name: 'Veeam Agent',
                note: 'Free for personal Windows use'
              }, {
                name: 'Duplicati',
                note: 'Open source, cross-platform'
              }]} safety="Store backup drives offline when not in use to protect against ransomware." data-id="element-4896" />
                <ToolCategory title="NAS (Network Attached Storage)" icon={Database} desc="Private cloud storage for home or office." tools={[{
                name: 'Synology',
                note: 'User-friendly, feature-rich'
              }, {
                name: 'QNAP',
                note: 'Advanced hardware specs'
              }, {
                name: 'TrueNAS',
                note: 'Open source, DIY option'
              }]} safety="Configure RAID for redundancy and enable snapshots for ransomware protection." data-id="element-4897" />
              </div>
            </section>

            {/* Implementation Guide */}
            <section data-id="element-4898">
              <h2 className="text-2xl font-bold text-slate-100 mb-6" data-id="element-4899">
                Implementation Guide
              </h2>
              <div className="space-y-4" data-id="element-4900">
                <Step number={1} text="Choose a cloud backup service (Backblaze recommended for simplicity)." data-id="element-4901" />
                <Step number={2} text="Set up automatic daily backups." data-id="element-4902" />
                <Step number={3} text="Configure a local backup to an external drive (weekly)." data-id="element-4903" />
                <Step number={4} text="Create an offline backup (monthly) and disconnect the drive." data-id="element-4904" />
                <Step number={5} text="Test restoring a file from each backup location quarterly." data-id="element-4905" />
              </div>
            </section>

            {/* Common Mistakes */}
            <section data-id="element-4906">
              <h2 className="text-2xl font-bold text-slate-100 mb-6" data-id="element-4907">
                Common Mistakes
              </h2>
              <div className="bg-red-500/10 border border-red-500/20 p-6 rounded-lg" data-id="element-4908">
                <ul className="space-y-3 text-slate-300" data-id="element-4909">
                  <li className="flex items-start" data-id="element-4910">
                    <AlertTriangle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" data-id="element-4911" />
                    Confusing cloud sync (Dropbox) with cloud backup
                    (Backblaze).
                  </li>
                  <li className="flex items-start" data-id="element-4912">
                    <AlertTriangle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" data-id="element-4913" />
                    Never testing restores (you don't have a backup until you
                    restore it).
                  </li>
                  <li className="flex items-start" data-id="element-4914">
                    <AlertTriangle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" data-id="element-4915" />
                    Leaving backup drives permanently connected (ransomware
                    risk).
                  </li>
                </ul>
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-6" data-id="element-4916">
            <Card className="p-6 sticky top-24" data-id="element-4917">
              <h3 className="text-lg font-bold text-slate-100 mb-4" data-id="element-4918">
                Quick Actions
              </h3>
              <Link to="/personal/data-backup" className="block w-full" data-id="element-4919">
                <Button className="w-full bg-teal-500 hover:bg-teal-600 text-slate-950" data-id="element-4920">
                  Backup Strategy Guide <ArrowRight className="ml-2 h-4 w-4" data-id="element-4921" />
                </Button>
              </Link>
              <Link to="/organization/recover" className="block w-full mt-3" data-id="element-4922">
                <Button variant="secondary" className="w-full" data-id="element-4923">
                  Business Recovery
                </Button>
              </Link>

              <div className="mt-8 pt-6 border-t border-slate-800" data-id="element-4924">
                <h4 className="text-sm font-bold text-slate-400 uppercase mb-4" data-id="element-4925">
                  Related
                </h4>
                <ul className="space-y-2 text-sm text-slate-400" data-id="element-4926">
                  <li data-id="element-4927">
                    <Link to="/devices/windows" className="hover:text-teal-500" data-id="element-4928">
                      Windows Security
                    </Link>
                  </li>
                  <li data-id="element-4929">
                    <Link to="/devices/macos" className="hover:text-teal-500" data-id="element-4930">
                      macOS Security
                    </Link>
                  </li>
                </ul>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </Layout>;
}
function ToolCategory({
  title,
  icon: Icon,
  desc,
  tools,
  safety
}: {
  title: string;
  icon: any;
  desc: string;
  tools: {
    name: string;
    note: string;
  }[];
  safety: string;
}) {
  return <div className="bg-slate-900 p-6 rounded-lg border border-slate-800" data-id="element-4931">
      <div className="flex items-start mb-4" data-id="element-4932">
        <div className="bg-teal-500/10 p-3 rounded-lg mr-4 flex-shrink-0" data-id="element-4933">
          <Icon className="h-6 w-6 text-teal-500" data-id="element-4934" />
        </div>
        <div data-id="element-4935">
          <h3 className="text-lg font-bold text-slate-200 mb-1" data-id="element-4936">{title}</h3>
          <p className="text-slate-400 text-sm" data-id="element-4937">{desc}</p>
        </div>
      </div>

      <div className="space-y-3 mb-6 pl-16" data-id="element-4938">
        {tools.map((tool, idx) => <div key={idx} className="flex justify-between items-center text-sm border-b border-slate-800 pb-2 last:border-0 last:pb-0" data-id="element-4939">
            <span className="font-bold text-slate-300" data-id="element-4940">{tool.name}</span>
            <span className="text-slate-500" data-id="element-4941">{tool.note}</span>
          </div>)}
      </div>

      <div className="bg-slate-800/50 p-3 rounded text-xs text-slate-400 flex items-start" data-id="element-4942">
        <Shield className="h-4 w-4 text-teal-500 mr-2 flex-shrink-0" data-id="element-4943" />
        <span data-id="element-4944">
          <strong data-id="element-4945">Safety Note:</strong> {safety}
        </span>
      </div>
    </div>;
}
function Step({
  number,
  text
}: {
  number: number;
  text: string;
}) {
  return <div className="flex items-center bg-slate-900/50 p-4 rounded border border-slate-800" data-id="element-4946">
      <div className="w-8 h-8 rounded-full bg-slate-800 text-teal-500 font-bold flex items-center justify-center mr-4 flex-shrink-0 border border-slate-700" data-id="element-4947">
        {number}
      </div>
      <p className="text-slate-300" data-id="element-4948">{text}</p>
    </div>;
}